/**
 * NotificationBell — Üst bar bildirim zili. SADECE trigger + Popover(desktop)/
 * Sheet(mobile) kablolaması. Veri çekme + filtre + gruplama + state
 * NotificationFeed'de yaşar (bkz. ../notifications/NotificationFeed.jsx).
 *
 * Desktop: Popover dropdown (shadcn/ui)
 * Mobile : Sheet bottom drawer
 *
 * API: GET /api/notifications (useNotifications hook, NotificationFeed içinde)
 *       PATCH /api/notifications/{id}/read (useMarkRead)
 *       POST /api/notifications/mark-all-read (useMarkAllRead)
 *
 * Props:
 *   variant : 'desktop' | 'mobile'  (default: 'desktop')
 *   isDark  : landing'in koyu/saydam navbar varyantı (Navbar.js'den) — sadece
 *             idle/hover ikon rengini etkiler, unread rozeti değişmez.
 */
import { useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotifications } from '../../hooks/cabin/useNotifications';
import { interpolate } from '../../utils/cabinI18n';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Sheet, SheetTrigger, SheetContent } from '../ui/sheet';
import NotificationFeed from '../notifications/NotificationFeed';

// ── Unread badge (çan üstü sayaç) ──────────────────────────────────────────────
function UnreadBadge({ count }) {
  if (!count) return null;
  const label = count >= 10 ? '9+' : String(count);
  return (
    <span
      aria-hidden="true"
      className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-rose-600 text-white text-[10px] font-black rounded-full px-0.5 border-2 border-white"
    >
      {label}
    </span>
  );
}

export default function NotificationBell({ variant = 'desktop', isDark = false }) {
  const { t } = useLanguage();
  const { data } = useNotifications();
  const unreadCount = data?.unread_count || 0;

  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(false);
  const bellRef = useRef(null);

  const ariaLabel = unreadCount
    ? interpolate(t('cabin.notifications.bell_label_count'), { count: unreadCount })
    : t('cabin.notifications.bell_label');

  // Koyu/saydam landing navbar varyantı: idle #B9C4D4 (navLinkIdle ile aynı
  // mevcut hex, yeni token icat edilmedi), hover/focus #F7F5F0.
  const idleClass = isDark
    ? 'text-[#B9C4D4] hover:text-[#F7F5F0]'
    : 'text-gray-500 hover:text-[var(--brand-primary)]';

  const bellButton = (ref, expanded) => (
    <button
      ref={ref}
      type="button"
      aria-label={ariaLabel}
      aria-expanded={expanded}
      data-testid="cabin-notification-bell"
      className={`relative p-2 rounded-xl hover:bg-gray-100 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-w-[44px] min-h-[44px] flex items-center justify-center ${idleClass}`}
    >
      <Bell size={20} aria-hidden="true" />
      <UnreadBadge count={unreadCount} />
    </button>
  );

  // aria-live EKLENDİ: panel açık/kapalı fark etmeksizin DOM'da kalıcı —
  // 60sn poll sırasında gelen bildirim ekran okuyucuya duyurulsun.
  const liveMessage =
    unreadCount > 0
      ? interpolate(t('cabin.notifications.sr_live_unread'), { count: unreadCount })
      : t('cabin.notifications.sr_live_all_read');
  const liveRegion = (
    <span className="sr-only" aria-live="polite" aria-atomic="true">
      {liveMessage}
    </span>
  );

  if (variant === 'mobile') {
    return (
      <>
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>{bellButton(bellRef, mobileOpen)}</SheetTrigger>
          <SheetContent
            side="bottom"
            className="p-0 h-[80vh] rounded-t-2xl flex flex-col"
            aria-label={t('cabin.notifications.panel_label')}
          >
            <div
              aria-hidden="true"
              className="mx-auto mt-2 mb-1 h-1.5 w-10 rounded-full bg-gray-300 shrink-0"
            />
            <div className="flex-1 min-h-0 flex flex-col">
              <NotificationFeed
                density="compact"
                onClose={() => setMobileOpen(false)}
                showViewAllLink
              />
            </div>
          </SheetContent>
        </Sheet>
        {liveRegion}
      </>
    );
  }

  // Desktop: Popover (controlled — bildirime tıklayınca kapanabilsin)
  return (
    <>
      <Popover open={desktopOpen} onOpenChange={setDesktopOpen}>
        <PopoverTrigger asChild>{bellButton(bellRef, desktopOpen)}</PopoverTrigger>
        <PopoverContent
          align="end"
          sideOffset={8}
          className="p-0 w-80 max-h-[480px] rounded-2xl border-2 border-gray-200 shadow-[0_8px_24px_rgba(0,0,0,0.12)] overflow-hidden flex flex-col"
        >
          {/* min-h-0 ZORUNLU: PopoverContent'in `max-h-[480px]` ile
              sınırlanan yüksekliği CSS açısından "definite" değildir (max-height
              yalnızca bir üst sınır — percentage-height/flex-basis çözümlemesi
              için "auto" sayılır). Bu wrapper olmadan NotificationFeed'in kendi
              kök div'i (`h-full`) 480px'e değil İÇERİĞİN boyuna (ör. 2372px)
              büyüyor, "Tümünü Gör" footer'ı görünür alanın çok altına düşüyor —
              scroll konteynerdeki min-h-0 tek başına yetmiyor, çünkü onun
              ATASI zaten büyümüş oluyor. Mobile Sheet zaten aynı wrapper'ı
              kullanıyor (bkz. aşağıdaki variant='mobile' dalı) — Popover'da
              eksikti, buradaki asıl bug buydu. flex-1 + min-h-0 birlikte bu
              div'i PopoverContent'in flex-column içinde gerçek/definite bir
              yüksekliğe oturtur; NotificationFeed'in kendi h-full'ü SONRA bu
              definite yüksekliğe göre doğru çözülür. */}
          <div className="flex-1 min-h-0 flex flex-col">
            <NotificationFeed
              density="compact"
              onClose={() => setDesktopOpen(false)}
              showViewAllLink
            />
          </div>
        </PopoverContent>
      </Popover>
      {liveRegion}
    </>
  );
}
